import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { GraduationCap, ArrowLeft, Check, Mail } from "lucide-react";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import { useT } from "@/context/I18nContext";

export default function StudentDiscount() {
  const nav = useNavigate();
  const { locale } = useT();
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/student/verify", { email });
      setSent(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || (locale === "en" ? "Error" : "Errore"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen text-slate-100">
      <Navbar />
      <main className="mx-auto max-w-2xl px-4 py-16 sm:px-6">
        <button onClick={() => nav("/")} className="mb-6 flex items-center gap-2 text-sm text-slate-400 hover:text-cyan-300">
          <ArrowLeft className="h-4 w-4" /> {locale === "en" ? "Back to home" : "Torna alla home"}
        </button>

        <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-8 glow-cyan">
          <div className="flex items-center gap-3">
            <div className="grid h-12 w-12 place-items-center rounded-xl bg-gradient-to-br from-emerald-400 to-cyan-500">
              <GraduationCap className="h-6 w-6 text-slate-950" />
            </div>
            <div>
              <div className="text-xs uppercase tracking-widest text-emerald-300">
                {locale === "en" ? "STUDENT DISCOUNT" : "SCONTO STUDENTI"}
              </div>
              <h1 className="text-2xl font-bold">
                {locale === "en" ? "Get 20% off with your academic email" : "20% di sconto con la tua email accademica"}
              </h1>
            </div>
          </div>

          <ul className="mt-6 space-y-2 text-sm text-slate-300">
            <li className="flex items-center gap-2"><Check className="h-4 w-4 text-emerald-300" />
              {locale === "en" ? "Accepted: .edu, .ac.uk and major Italian universities (unimi.it, polimi.it, bocconi.it, ...)" : "Accettate: .edu, .ac.uk e principali universita italiane (unimi.it, polimi.it, bocconi.it, ...)"}
            </li>
            <li className="flex items-center gap-2"><Check className="h-4 w-4 text-emerald-300" />
              {locale === "en" ? "Discount valid on any plan, forever" : "Sconto valido su qualsiasi piano, per sempre"}
            </li>
            <li className="flex items-center gap-2"><Check className="h-4 w-4 text-emerald-300" />
              {locale === "en" ? "One-click activation after email verification" : "Attivazione con un click dopo la verifica email"}
            </li>
          </ul>

          {sent ? (
            <div data-testid="student-sent-msg" className="mt-6 rounded-xl border border-cyan-500/40 bg-cyan-500/10 p-6 text-center">
              <Mail className="mx-auto h-8 w-8 text-cyan-300" />
              <h3 className="mt-3 font-semibold">
                {locale === "en" ? "Check your inbox" : "Controlla la tua email"}
              </h3>
              <p className="mt-1 text-sm text-slate-300">
                {locale === "en"
                  ? `We sent a verification link to ${email}. Click it to activate the 20% discount.`
                  : `Ti abbiamo inviato un link di verifica a ${email}. Cliccalo per attivare lo sconto del 20%.`}
              </p>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-6 space-y-4">
              <div>
                <Label htmlFor="stu-email" className="text-slate-300">
                  {locale === "en" ? "Your academic email" : "La tua email accademica"}
                </Label>
                <Input
                  id="stu-email"
                  type="email"
                  required
                  data-testid="student-email-input"
                  placeholder="mario.rossi@unimi.it"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="mt-1 border-slate-800 bg-slate-950 text-slate-100"
                />
              </div>
              <Button
                type="submit"
                disabled={busy}
                data-testid="student-submit-btn"
                className="w-full bg-emerald-400 text-slate-950 hover:bg-emerald-300"
              >
                {busy ? (locale === "en" ? "Sending..." : "Invio...") : (locale === "en" ? "Send verification link" : "Invia link di verifica")}
              </Button>
            </form>
          )}
        </div>
      </main>
    </div>
  );
}
